import { useMemo } from "react";
import { useUser } from "../contexts/UserContext";
import { PrimaryUseCase } from "../types";
import { NavItem } from "./useNavItems";

export interface FocusProfile {
  /** Tab-based nav items — these replace the normal nav bar (Home is always first) */
  tabNav: NavItem[];
  /** Which hero types to show on the home page (first = full-bleed) */
  homeHeroes: string[];
  /** Whether to show wellness section on home */
  showWellness: boolean;
  /** Label for the focus */
  label: string;
}

/** Home tab — always first in every focus profile */
const HOME_TAB: NavItem = { id: "focus__home", label: "Today", icon: "fa-solid fa-house", path: "/" };

/**
 * Focus profiles turn the app into a single-purpose tool.
 * The home page always exists and shows the primary hero.
 * The focused page's tabs fill the rest of the nav bar.
 */
const FOCUS_PROFILES: Record<string, FocusProfile> = {
  fitness: {
    label: "Fitness",
    homeHeroes: ["fitness"],
    showWellness: false,
    tabNav: [
      HOME_TAB,
      { id: "fitness__log", label: "Gym", icon: "fa-solid fa-dumbbell", path: "/fitness?tab=log" },
      { id: "fitness__planner", label: "Planner", icon: "fa-solid fa-clipboard-list", path: "/fitness?tab=planner" },
      { id: "fitness__health", label: "Health", icon: "fa-solid fa-heart-pulse", path: "/fitness?tab=health" },
      { id: "fitness__progress", label: "Progress", icon: "fa-solid fa-chart-line", path: "/fitness?tab=progress" },
    ],
  },
  running: {
    label: "Running",
    homeHeroes: ["running"],
    showWellness: false,
    tabNav: [
      HOME_TAB,
      { id: "running__plan", label: "Plan", icon: "fa-solid fa-calendar-week", path: "/running?tab=plan" },
      { id: "running__track", label: "Run", icon: "fa-solid fa-person-running", path: "/running?tab=track" },
      { id: "running__stats", label: "Stats", icon: "fa-solid fa-chart-column", path: "/running?tab=stats" },
      { id: "running__records", label: "Records", icon: "fa-solid fa-medal", path: "/running?tab=records" },
      { id: "running__history", label: "History", icon: "fa-solid fa-clock-rotate-left", path: "/running?tab=history" },
    ],
  },
  health: {
    label: "Women's Health",
    homeHeroes: ["cycle"],
    showWellness: true,
    tabNav: [
      HOME_TAB,
      { id: "womens__cycle", label: "Cycle", icon: "fa-solid fa-seedling", path: "/womens-health?tab=cycle" },
      { id: "womens__analytics", label: "Trends", icon: "fa-solid fa-chart-line", path: "/womens-health?tab=analytics" },
      { id: "womens__insights", label: "Insights", icon: "fa-solid fa-lightbulb", path: "/womens-health?tab=insights" },
      { id: "womens__settings", label: "More", icon: "fa-solid fa-ellipsis", path: "/womens-health?tab=settings" },
    ],
  },
  notesandtasks: {
    label: "Notes & Tasks",
    homeHeroes: ["tasks"],
    showWellness: false,
    tabNav: [
      HOME_TAB,
      { id: "focus__notes", label: "Notes", icon: "fa-solid fa-note-sticky", path: "/notes/" },
      { id: "focus__tasks", label: "Tasks", icon: "fa-solid fa-list-check", path: "/tasks/" },
      { id: "focus__calendar", label: "Calendar", icon: "fa-solid fa-calendar-days", path: "/calendar/" },
    ],
  },
  notes: {
    label: "Notes",
    homeHeroes: ["tasks"],
    showWellness: false,
    tabNav: [
      HOME_TAB,
      { id: "focus__notes", label: "Notes", icon: "fa-solid fa-note-sticky", path: "/notes/" },
      { id: "focus__tasks", label: "Tasks", icon: "fa-solid fa-list-check", path: "/tasks/" },
      { id: "focus__calendar", label: "Calendar", icon: "fa-solid fa-calendar-days", path: "/calendar/" },
    ],
  },
  housework: {
    label: "Tasks",
    homeHeroes: ["tasks"],
    showWellness: false,
    tabNav: [
      HOME_TAB,
      { id: "focus__tasks", label: "Tasks", icon: "fa-solid fa-list-check", path: "/tasks/" },
      { id: "focus__notes", label: "Notes", icon: "fa-solid fa-note-sticky", path: "/notes/" },
      { id: "focus__calendar", label: "Calendar", icon: "fa-solid fa-calendar-days", path: "/calendar/" },
    ],
  },
  wellness: {
    label: "Wellness",
    homeHeroes: ["cycle"],
    showWellness: true,
    tabNav: [
      HOME_TAB,
      { id: "health__log", label: "Log", icon: "fa-solid fa-pen-to-square", path: "/health?tab=log" },
      { id: "health__trends", label: "Trends", icon: "fa-solid fa-chart-line", path: "/health?tab=trends" },
      { id: "focus__calendar", label: "Calendar", icon: "fa-solid fa-calendar-days", path: "/calendar/" },
    ],
  },
  weightloss: {
    label: "Weight Loss",
    homeHeroes: ["fitness", "running"],
    showWellness: true,
    tabNav: [
      HOME_TAB,
      { id: "fitness__log", label: "Gym", icon: "fa-solid fa-dumbbell", path: "/fitness?tab=log" },
      { id: "fitness__progress", label: "Progress", icon: "fa-solid fa-chart-line", path: "/fitness?tab=progress" },
      { id: "health__log", label: "Health", icon: "fa-solid fa-heart-pulse", path: "/health" },
      { id: "running__track", label: "Run", icon: "fa-solid fa-person-running", path: "/running?tab=track" },
    ],
  },
};

interface UseFocusModeResult {
  /** Whether focus mode is active */
  focusModeEnabled: boolean;
  /** The active focus profile (null when focus mode is off) */
  profile: FocusProfile | null;
  /** The user's primary use case */
  primaryUseCase: PrimaryUseCase;
}

const useFocusMode = (): UseFocusModeResult => {
  const userContext = useUser();
  const prefs = userContext?.userDetails?.onboarding?.preferences;
  const primaryUseCase = prefs?.primaryUseCase ?? null;
  const userChoseFocus = prefs?.focusMode ?? false;

  const profile = useMemo((): FocusProfile | null => {
    if (!userChoseFocus) return null;
    if (!primaryUseCase) return null;
    return FOCUS_PROFILES[primaryUseCase] ?? null;
  }, [userChoseFocus, primaryUseCase]);

  return {
    focusModeEnabled: userChoseFocus && !!profile,
    profile,
    primaryUseCase,
  };
};

export default useFocusMode;
